import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, Alert, Spinner, InputGroup } from "react-bootstrap";
import axios from "axios";
import logo from "../assets/img/unnamed.png";
import bg2 from "../assets/img/bg02.jpg";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

export default function Signin2() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    if (error) setError("");
  }, [error]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Vérifier d'abord si MFA est actif
      const mfaResponse = await apiClient.post("/bo/login-mfa", {
        email: formData.email,
        password: formData.password
      });

      const hasMfa = mfaResponse.data.has_mfa === true || mfaResponse.data.has_mfa === 'YES';

      // Si MFA actif, rediriger vers la page OTP
      if (hasMfa) {
        sessionStorage.setItem("pendingLogin", JSON.stringify({
          email: formData.email,
          password: formData.password,
          timestamp: Date.now()
        }));

        navigate("/apps/validationOTP", { 
          state: { 
            userData: { email: formData.email },
            requiresOtp: true
          }
        });
        return;
      }

      // Sinon, on initie la connexion directe
      const loginResponse = await apiClient.post("/bo/login", {
        email: formData.email,
        password: formData.password
      });

      const data = loginResponse.data;
      const token = data.access_token || data.token;

      if (!token) {
        throw new Error("Token non reçu");
      }

      // Stockage des données pour la session
      localStorage.setItem("authToken", token);
      if (data.refresh_token) localStorage.setItem("refreshToken", data.refresh_token);
      if (data.user) localStorage.setItem("userData", JSON.stringify(data.user));
      if (data.business) localStorage.setItem("businessData", JSON.stringify(data.business));

      // Redirection
      const redirectPath = data.business?.kyc === 'OK'
        ? (data.user?.acc_type === 'business' ? "/dashboard/analytics" : "/dashboard/finance")
        : "/apps/MonCompte";
      
      navigate(redirectPath);

    } catch (err) {
      console.error("Erreur détaillée:", err);
      
      // Gestion des erreurs avec messages précis
      if (err.response) {
        // Erreur avec réponse du serveur
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data?.error;
        
        if (status === 401) {
          setError("Email ou mot de passe incorrect");
        } else if (status === 422) {
          setError("Données invalides");
        } else {
          setError(message || `Erreur serveur ${status}`);
        }
      } else if (err.code === 'ECONNABORTED') {
        setError("Délai d'attente dépassé. Le serveur ne répond pas.");
      } else if (err.message) {
        // Afficher le vrai message d'erreur pour tous les cas
        if (err.message.includes('Content Security Policy') || err.message.includes('CSP')) {
          setError(`Erreur de sécurité CSP: L'application n'est pas autorisée à communiquer avec "${API_BASE_URL}". 
                   Domaines autorisés: Vérifiez la politique de sécurité.`);
        } else if (err.message.includes('Network Error')) {
          setError(`Erreur réseau: Impossible de joindre le serveur. Vérifiez que l'URL "${API_BASE_URL}" est correcte et accessible.`);
        } else if (err.message.includes('Network')) {
          setError(`Erreur réseau: ${err.message}. Vérifiez votre connexion internet.`);
        } else if (err.message.includes('CORS')) {
          setError(`Erreur CORS: Le serveur n'autorise pas les requêtes depuis cette origine.`);
        } else {
          setError(`Erreur technique: ${err.message}`);
        }
      } else {
        setError("Erreur de connexion inattendue");
      }
    } finally {
      setLoading(false);
    }
  }, [formData.email, formData.password, navigate]);

  return (
    <div className="page-sign d-block py-0">
      <Row className="g-0">
        <Col md="7" lg="5" xl="4" className="col-wrapper">
          <Card className="card-sign shadow-lg border-0 rounded-4">
            <Card.Header className="bg-white border-0 text-center">
              <Link to="/" className="header-logo mb-4 fs-4 fw-bold text-primary">
                BULK SMS V1
              </Link>
              <p><img src={logo} style={{ height: "80px", borderRadius:"100%", border: "3px solid blue" }} alt="Logo" /></p>
              
              <Card.Title className="mb-2 fs-3 fw-semibold">
                Connexion
              </Card.Title>
              <Card.Text className="text-muted">
                Heureux de vous revoir 👋 Veuillez entrer vos identifiants.
              </Card.Text>
            </Card.Header>

            <Card.Body>
              {error && (
                <Alert variant="danger" className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div style={{ flex: 1 }}>
                    <div className="small fw-bold">{error}</div>
                    {error.includes('CSP') && (
                      <div className="small mt-2 p-2 bg-danger bg-opacity-10 rounded">
                        <strong>🔧 Solution:</strong> Ajoutez "{API_BASE_URL}" à la directive connect-src de votre Content Security Policy.
                      </div>
                    )}
                    {error.includes('réseau') && !error.includes('CSP') && (
                      <div className="small mt-2">
                        <strong>URL configurée:</strong> {API_BASE_URL || 'Non définie'}
                      </div>
                    )}
                  </div>
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                <div className="mb-4">
                  <Form.Label>Adresse e-mail</Form.Label>
                  <Form.Control 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Entrez votre adresse e-mail"
                    disabled={loading}
                    required
                  />
                </div>

                <div className="mb-4">
                  <Form.Label className="d-flex justify-content-between">
                    Mot de passe
                    <Link to="/pages/forgot" className="text-decoration-none">
                      Mot de passe oublié ?
                    </Link>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control 
                      type={showPassword ? "text" : "password"} 
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Entrez votre mot de passe"
                      disabled={loading}
                      required
                    />
                    <InputGroup.Text 
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                    </InputGroup.Text>
                  </InputGroup>
                </div>

                <Button 
                  type="submit" 
                  className="w-100 mb-3"
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Connexion...
                    </>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </Form>

              {/* Informations de débogage visibles uniquement en développement */}
              {/* {process.env.NODE_ENV === 'development' && (
                <div className="mt-3 p-2 bg-light border rounded small">
                  <div><strong>🔧 Debug:</strong></div>
                  <div>URL API: {API_BASE_URL || 'Non définie'}</div>
                  <div>Environnement: {process.env.NODE_ENV}</div>
                </div>
              )} */}
            </Card.Body>

            <Card.Footer className="bg-white border-0 text-center">
              <span className="text-muted">Pas encore de compte ? </span>
              <Link to="/pages/register" className="fw-semibold text-primary">
                Créer un compte
              </Link>
            </Card.Footer>
          </Card>
        </Col>

        <Col className="d-none d-lg-block">
          <img src={bg2} className="auth-img vh-100 object-fit-cover" alt="Background" />
        </Col>
      </Row>
    </div>
  );
}